import { Vec2 } from "./types.ts";
import { PERFORMANCE } from "./config.ts";

interface PathNode {
  x: number;
  y: number;
  g: number;
  h: number;
  f: number;
  parent: PathNode | null;
}

export interface PathGrid {
  width: number;
  height: number;
  isBlocked(x: number, y: number): boolean;
  getCost?(x: number, y: number): number;
}

/**
 * A* pathfinding with LRU cache.
 * Pure function interface — no side effects, fully testable.
 */
export class Pathfinding {
  private cache = new LRUCache<string, Vec2[] | null>(
    PERFORMANCE.PATH_CACHE_SIZE,
  );

  findPath(grid: PathGrid, start: Vec2, end: Vec2): Vec2[] | null {
    const key = `${start.x},${start.y}-${end.x},${end.y}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;

    const result = this.astar(grid, start, end);
    this.cache.set(key, result);
    return result;
  }

  /** Like findPath but never reads or writes the cache. Required for callers
   *  that pass a transient overlay grid where the key (start→end) collides
   *  with the canonical real-grid path. */
  findPathUncached(grid: PathGrid, start: Vec2, end: Vec2): Vec2[] | null {
    return this.astar(grid, start, end);
  }

  canPlace(
    grid: PathGrid,
    entries: Vec2[],
    exits: Vec2[],
    blockX: number,
    blockY: number,
  ): boolean {
    // Temporarily consider blockX,blockY as blocked
    const testGrid: PathGrid = {
      width: grid.width,
      height: grid.height,
      isBlocked(x: number, y: number): boolean {
        if (x === blockX && y === blockY) return true;
        return grid.isBlocked(x, y);
      },
    };

    // Every entry must still reach at least one exit
    for (const entry of entries) {
      let canReachAny = false;
      for (const exit of exits) {
        if (this.astar(testGrid, entry, exit) !== null) {
          canReachAny = true;
          break;
        }
      }
      if (!canReachAny) return false;
    }
    return true;
  }

  invalidateCache(): void {
    this.cache.clear();
  }

  private astar(grid: PathGrid, start: Vec2, end: Vec2): Vec2[] | null {
    const openHeap = new MinHeap<PathNode>((a, b) => a.f - b.f);
    const closedSet = new Set<string>();
    const openLookup = new Map<string, PathNode>();

    const startNode: PathNode = {
      x: start.x,
      y: start.y,
      g: 0,
      h: this.heuristic(start, end),
      f: this.heuristic(start, end),
      parent: null,
    };
    openHeap.push(startNode);
    openLookup.set(`${start.x},${start.y}`, startNode);

    while (openHeap.size > 0) {
      const current = openHeap.pop()!;
      const currentKey = `${current.x},${current.y}`;
      openLookup.delete(currentKey);

      if (current.x === end.x && current.y === end.y) {
        return this.reconstructPath(current);
      }

      closedSet.add(currentKey);

      for (const neighbor of this.getNeighbors(current, grid)) {
        const key = `${neighbor.x},${neighbor.y}`;
        if (closedSet.has(key)) continue;

        const moveCost = grid.getCost
          ? grid.getCost(neighbor.x, neighbor.y)
          : 1;
        const g = current.g + moveCost;
        const existing = openLookup.get(key);

        if (!existing) {
          const h = this.heuristic(neighbor, end);
          const node: PathNode = {
            x: neighbor.x,
            y: neighbor.y,
            g,
            h,
            f: g + h,
            parent: current,
          };
          openHeap.push(node);
          openLookup.set(key, node);
        } else if (g < existing.g) {
          existing.g = g;
          existing.f = g + existing.h;
          existing.parent = current;
          openHeap.decreaseKey(existing);
        }
      }
    }

    return null;
  }

  private heuristic(a: Vec2, b: Vec2): number {
    return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  }

  private static readonly DIRS = [
    { x: 0, y: -1 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: -1, y: 0 },
  ] as const;

  private getNeighbors(node: PathNode, grid: PathGrid): Vec2[] {
    const result: Vec2[] = [];
    for (const dir of Pathfinding.DIRS) {
      const nx = node.x + dir.x;
      const ny = node.y + dir.y;
      if (
        nx >= 0 &&
        nx < grid.width &&
        ny >= 0 &&
        ny < grid.height &&
        !grid.isBlocked(nx, ny)
      ) {
        result.push({ x: nx, y: ny });
      }
    }
    return result;
  }

  private reconstructPath(node: PathNode): Vec2[] {
    const path: Vec2[] = [];
    let current: PathNode | null = node;
    while (current !== null) {
      path.push({ x: current.x, y: current.y });
      current = current.parent;
    }
    path.reverse();
    return path;
  }
}

class LRUCache<K, V> {
  private capacity: number;
  private cache = new Map<K, V>();

  constructor(capacity: number) {
    this.capacity = capacity;
  }

  get(key: K): V | undefined {
    if (!this.cache.has(key)) return undefined;
    const value = this.cache.get(key)!;
    // Move to end (most recent)
    this.cache.delete(key);
    this.cache.set(key, value);
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.capacity) {
      // Evict oldest (first entry)
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }
}

class MinHeap<T> {
  private items: T[] = [];
  private compare: (a: T, b: T) => number;

  constructor(compare: (a: T, b: T) => number) {
    this.compare = compare;
  }

  get size(): number {
    return this.items.length;
  }

  push(item: T): void {
    this.items.push(item);
    this.bubbleUp(this.items.length - 1);
  }

  pop(): T | undefined {
    if (this.items.length === 0) return undefined;
    const top = this.items[0]!;
    const last = this.items.pop()!;
    if (this.items.length > 0) {
      this.items[0] = last;
      this.sinkDown(0);
    }
    return top;
  }

  rebuild(): void {
    for (let i = Math.floor(this.items.length / 2) - 1; i >= 0; i--) {
      this.sinkDown(i);
    }
  }

  decreaseKey(item: T): void {
    const idx = this.items.indexOf(item);
    if (idx !== -1) this.bubbleUp(idx);
  }

  private bubbleUp(idx: number): void {
    while (idx > 0) {
      const parent = (idx - 1) >> 1;
      if (this.compare(this.items[idx]!, this.items[parent]!) < 0) {
        [this.items[idx], this.items[parent]] = [
          this.items[parent]!,
          this.items[idx]!,
        ];
        idx = parent;
      } else {
        break;
      }
    }
  }

  private sinkDown(idx: number): void {
    const length = this.items.length;
    while (true) {
      let smallest = idx;
      const left = 2 * idx + 1;
      const right = 2 * idx + 2;
      if (
        left < length &&
        this.compare(this.items[left]!, this.items[smallest]!) < 0
      ) {
        smallest = left;
      }
      if (
        right < length &&
        this.compare(this.items[right]!, this.items[smallest]!) < 0
      ) {
        smallest = right;
      }
      if (smallest !== idx) {
        [this.items[idx], this.items[smallest]] = [
          this.items[smallest]!,
          this.items[idx]!,
        ];
        idx = smallest;
      } else {
        break;
      }
    }
  }
}
